import React, { PropsWithChildren, ReactElement, useLayoutEffect, useState } from 'react';
import { act, render, RenderOptions, RenderResult } from '@testing-library/react';
import {
  BimTrackThemeProvider,
  FontSet,
  OverlayBanner,
  OverlayBannerProvider,
  ResetCSS,
  ToastStack,
  ToastStackProvider,
} from '@bimone/bimtrackreactcomponents';
import { applicationStore } from '../../app/redux/store';
import { Provider } from 'react-redux';
import { I18n, I18nProvider } from '@bimone/bimtrack-core';
import { FormatFunction, Resource } from 'i18next';
import { Store } from 'redux';

export const COMMON_NAMESPACE = 'common';

export type TestWrapperProps = PropsWithChildren<{
  store?: Store;
  translations?: Resource;
  defaultNS?: string;
  fallbackNS?: string;
  formatter?: FormatFunction;
}>;

export const TestWrapper = ({
  children,
  translations,
  store = applicationStore,
  defaultNS = COMMON_NAMESPACE,
  fallbackNS = COMMON_NAMESPACE,
  formatter,
}: TestWrapperProps): JSX.Element => {
  const [i18n, setI18n] = useState<I18n>();

  useLayoutEffect(() => {
    const setTranslations = async (): Promise<void> => {
      const newI18n: I18n = await I18n.createOther(translations ?? {}, defaultNS, fallbackNS, formatter);

      act(() => {
        setI18n(newI18n);
      });
    };

    setTranslations();
  }, [defaultNS, fallbackNS, formatter, translations]);

  if (!i18n) {
    return <div>loading</div>;
  }

  return (
    <BimTrackThemeProvider>
      <FontSet />
      <ResetCSS />
      <Provider store={store}>
        <OverlayBannerProvider>
          <ToastStackProvider>
            <OverlayBanner />
            <ToastStack />
            <I18nProvider i18n={i18n}>{children}</I18nProvider>
          </ToastStackProvider>
        </OverlayBannerProvider>
      </Provider>
    </BimTrackThemeProvider>
  );
};

/*
  You can use this custom render as is, extend it in your project or use it as
  boilerplate (copy + modify in you project).
 */
const customRender = (
  ui: ReactElement,
  wrapperProps?: TestWrapperProps,
  testWrapperComponent: React.ComponentType<TestWrapperProps> = TestWrapper,
  options?: Omit<RenderOptions, 'queries'>
): RenderResult => {
  return render(ui, { wrapper: testWrapperComponent, ...options });
};

export { customRender as render };
